import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, FileText, Gamepad2, Bot, RefreshCcw } from "lucide-react";
import {
  createPodcast,
  getPodcast,
  useGetPodcast,
  getGetUserPodcastsQueryKey,
} from "@packages/api/madlenAPI";
import { getEnvironment } from "@packages/utils";
import PodcastAudioCard from "./PodcastAudioCard";
import { trackEvent } from "@packages/hooks/useAnalytics";
import { useToast } from "@packages/contexts/ToastContext";
import { Button, Text } from "@core/index";
import {
  CreatePodcastFromReferenceRequestDtoGenerationType,
  CreatePodcastRequestDtoContentLanguage,
  CreatePodcastRequestDtoGenerationType,
  CreatePodcastRequestDtoCefrLevel,
  PodcastResponseDto,
} from "@packages/api/madlenAPI.schemas";
import { useQueryClient } from "@tanstack/react-query";
import { FileUploader } from "@core/file-uploader";
import LoadingContainer from "@packages/components/Loading";
import { updateLastUsedTools } from "@packages/store/userSlice";
import { useAppDispatch } from "@packages/store/hooks";
import OutputLanguageSelector from "@features/tools/components/create-modal/OutputLanguageSelector";
import PodcastPlayerModal from "./PodcastPlayerModal";
import CEFRLevelSelector from "./CEFRLevelSelector";

export const exampleTopics = {
  tr: [
    "Dinozorların Gizemli Dünyası",
    "Gökyüzündeki Devler: Gezegenler ve Uydular",
    "Işık ve Optik: Gözlerimiz Nasıl Görür?",
    "Rüzgarın Gücü: Fırtınalar ve Kasırgalar",
    "Yeraltındaki Yaşam: Mağaralar ve Depremler",
    "Elektrik ve Manyetizma",
    "Buzullar ve Kutup Bölgeleri",
    "Ses Dalgaları: Müzik ve Fizik",
    "Mikroskobik Dünya: Hücreler ve DNA",
    "Bitkiler Nasıl Büyür? Fotosentezin Sırları",
    "İnsan Vücudu: Organlarımızın Çalışma Prensibi",
    "Yıldızların Doğumu ve Ölümü",
    "Zamanın Akışı: Saatler ve Takvimler",
    "Derin Deniz Keşifleri",
    "Mimari ve Mühendislik: Köprüler Nasıl Yapılır?",
    "Matematik Dünyası: Sayılar ve Geometri",
    "Robotların Yükselişi",
    "Hayvanların Hayatta Kalma Stratejileri",
    "İnsan Beyni: Zeka ve Hafıza",
    "Enerji Kaynakları: Yenilenebilir ve Fosil Yakıtlar",
    "Yer Altındaki Hazine: Mineraller ve Madenler",
    "Göktaşları ve Kuyruklu Yıldızlar",
    "Renklerin Bilimi: Pigmentler ve Işık Tayfı",
    "Zehirli Canlılar: Hayatta Kalma Mekanizmaları",
    "Gizemli Antik Uygarlıklar",
    "Volkanlar ve Lav Nehirleri",
    "Hava Durumu Nasıl Tahmin Edilir?",
    "Uzak Geçmişten Günümüze Evrim",
    "Tarihte Çığır Açan Kadın Bilim İnsanları",
    "Su Döngüsü ve Yaşam İçin Önemi",
    "İnsanların İlk Keşif Yolculukları",
    "Görünmeyen Güç: Atom ve Moleküller",
    "Zehirli Bitkiler ve Kullanım Alanları",
    "Kuyular ve Su Kaynakları",
    "Matematikte Sonsuzluk Kavramı",
    "İnsan Vücudundaki Mikroplar",
    "Süper Hızlı Hayvanlar: Çita ve Diğerleri",
    "Uçan Canlılar: Kuşlar, Yarasa ve Böcekler",
    "Yüzey Gerilimi ve Sıvıların Davranışı",
    "Denizlerin Derinliklerinde Yaşam",
    "Köpekbalıkları: Evrim ve Ekoloji",
    "Gizli Evren: Karanlık Madde ve Karanlık Enerji",
    "Makineler Nasıl Çalışır?",
    "Çizgi Romanlardaki Süper Güçlerin Gerçekliği",
    "Düşüncenin Gücü: Zihin ve Nörobilim",
    "Yerçekimsiz Ortamda Deneyler",
    "Yapay Zeka ve Gelecek Teknolojileri",
    "Denizanası ve Biyolüminesans",
    "Sosyal Böcekler: Karıncalar ve Arılar",
    "Elektronik Aletler Nasıl Çalışır?",
    "Tarihte Büyük Depremler ve Etkileri",
    "Hayalet Parçacıklar: Nötrinolar",
    "Dünya'nın Katmanları ve Levha Tektoniği",
    "İlk Bilgisayarlar ve Günümüz Teknolojisi",
    "Hayvanların Haberleşme Yöntemleri",
    "Büyük Sayılar: Googleplex Nedir?",
    "Hızlı Trenlerin Teknolojisi",
    "Ses Dalgalarının Kullanım Alanları",
    "Fizikte Kuantum Teorisi",
    "Göz Yanılmaları ve Algı",
    "Zehirli Mantarlar ve Doğadaki Rolü",
    "İnsanın Evrimi ve Atalarımız",
    "Tarihteki En Ünlü Materyaller: Altın, Elmas ve Uranyum",
    "Böceklerin Ekosisteme Katkısı",
    "Astronotların Günlük Hayatı",
    "Dünya'nın Manyetik Alanı ve Pusula",
    "Sesin Sıcaklık ve Ortama Göre Yayılması",
    "Ay'ın Evreleri ve Gelgit Olayları",
    "Alternatif Tıp ve Bitkisel Tedaviler",
    "Mars'ta Yaşam Mümkün mü?",
    "Antik Çin Buluşları ve Bilimi",
    "Hayvanlarda Kamuflaj ve Adaptasyon",
    "DNA'nın Keşfi ve Genetik Mühendisliği",
    "Yıldırımların Bilimi",
    "Nanoteknoloji: Küçük Ama Güçlü",
    "Dalgaların Gücü: Deniz ve Ses Dalgaları",
    "İnsanlığın İlk Yazılı Dilleri",
    "Rüzgar Türbinleri ve Yeşil Enerji",
    "Sesin Uzayda Yayılmaması",
    "Mumya Bilimi: Antik Mısır ve Mumyalama Teknikleri",
    "Antik Roma'nın Su Mühendisliği",
    "Zaman Yolculuğu: Bilim Kurgu mu Gerçek mi?",
    "Denizaltılar ve Batık Gemiler",
    "İnsan Duyuları ve Beyin Algısı",
    "Tarımdaki Devrimler ve Modern Teknoloji",
    "Sineklerin Görme Yetisi",
    "İnternetin Anatomisi: Veri Nasıl Akıyor?",
    "Güçlü Malzemeler: Karbon Fiber ve Grafen",
    "İnsanların İlk Evleri ve Yerleşimleri",
    "Duyguların Nörolojik Temelleri",
    "Karınca Kolonilerinin Organizasyonu",
    "Deniz Tuzu ve Okyanus Akıntıları",
    "İlk Teleskoplardan Günümüz Uzay Teleskoplarına",
    "Beyin ve Yapay Zeka Arasındaki Benzerlikler",
    "Deprem Dalgaları ve Sismoloji",
    "Çölde Hayatta Kalma Yöntemleri",
    "Derin Denizdeki Volkanlar ve Termal Bacalar",
    "Bilimkurgu Filmlerindeki Bilimsel Gerçeklik",
    "Uzay İstasyonlarının Çalışma Mekanizması",
    "Gezegenlerin Manyetik Alanları ve Koruyucu Etkileri",
    "Antik Yunan Filozofları ve Düşünce Akımları",
    "Osmanlı'nın Yükselişi ve Fetihler",
    "Rönesans'ın Sanat ve Bilime Katkıları",
    "Endüstri Devrimi: Makinaların Yükselişi",
    "Vikinglerin Keşif Yolculukları",
    "Antik Roma'da Günlük Yaşam",
    "İpek Yolu ve Kültürel Etkileşim",
    "Tarihteki En Büyük Savaşlar",
    "Eski Mısır'ın Tıp Bilimi",
    "Amerikan Bağımsızlık Savaşı'nın Sonuçları",
    "Hititler: Anadolu'nun Gizemli Krallığı",
    "Büyük İskender ve Kültürel Mirası",
    "Gutenberg ve Matbaanın İcadı",
    "Napolyon'un Avrupa'yı Değiştiren Seferleri",
    "İlk Kadın Hakları Hareketleri ve Feminist Tarih",
    "Moğol İmparatorluğu'nun Yönetim Sistemi",
    "İnka ve Maya Medeniyetleri",
    "Haçlı Seferleri: Nedenleri ve Sonuçları",
    "Sanayi Devrimi ve İşçi Hakları",
    "Soğuk Savaş: ABD ve SSCB Arasındaki Rekabet",
    "İkinci Dünya Savaşı'nın Bilim ve Teknolojiye Etkileri",
    "Osmanlı'da Eğitim ve Medrese Kültürü",
    "Kara Ölüm: Orta Çağ Avrupa'sında Veba Salgını",
    "Afrika Krallıkları: Mali, Songhai ve Benin",
    "Antik Mezopotamya ve İlk Yazı Sistemleri",
    "Altın Oran ve Sanattaki Kullanımı",
    "Kaos Teorisi ve Kelebek Etkisi",
    "Matematikte Sonsuzluk Paradoksları",
    "Euler'in Sayısı ve Doğadaki Yeri",
    "Geometri ve Doğadaki Şekiller",
    "Fraktaller ve Kendini Tekrarlayan Desenler",
    "İlk Bilgisayar Algoritmaları ve Alan Turing",
    "Sayı Teorisi ve Asal Sayılar",
    "İstatistik ve Olasılık Teorisi",
    "Diferansiyel Denklemler ve Fizikteki Önemi",
    "Pi Sayısının Keşfi ve Hesaplanması",
    "Tarih Boyunca Şifreleme Yöntemleri",
    "Matematikte Fermat'ın Son Teoremi",
    "Üçgenler ve Trigonometri'nin Gelişimi",
    "Venn Diyagramları ve Mantık Sistemleri",
    "İrrasyonel Sayılar ve Gerçek Dünya Kullanımı",
    "Büyük Sayılar: Googleplex ve Ötesi",
    "Matematikte Oyun Teorisi ve Strateji",
    "Matematik ve Kriptografi",
    "Türev ve İntegral: Kalkülüsün Doğuşu",
    "Elementlerin Keşfi ve Periyodik Tablo",
    "Kimyanın Günlük Hayattaki Yeri",
    "Radyoaktivitenin Keşfi ve Kullanımı",
    "Sıvılar ve Gazlar: Basınç ve Hareket",
    "Plastik ve Çevresel Etkileri",
    "Asitler ve Bazlar: pH Dengesi",
    "Endotermik ve Ekzotermik Tepkimeler",
    "Kimyasal Bağlar ve Maddenin Yapısı",
    "DNA ve Genetik Kimya",
    "Elementlerin İzotopları ve Kullanım Alanları",
    "Kimyasal Tepkimelerde Enerji ve Isı",
    "Polimerler ve Günlük Hayattaki Yeri",
    "Fosfor ve Patlayıcı Kimyasallar",
    "Organik Kimya ve Karbon Bileşikleri",
    "Metalurji ve Tarih Boyunca Metal Kullanımı",
    "Boya Pigmentleri ve Kimyası",
    "Antibiyotiklerin Keşfi ve Kimyası",
    "Kimyasal Reaksiyonların Hızını Etkileyen Faktörler",
    "Atmosferdeki Kimyasal Olaylar",
    "Gıda Kimyası: Yediğimiz Şeylerin İçeriği",
    "Kimyada Nobel Ödülleri ve Büyük Buluşlar",
    "Hidrojen Bağı ve Hayati Önemi",
    "Kimyanın Tıpta Kullanımı",
    "İlk Sentetik Kimyasal Bileşikler",
    "Sabun ve Deterjanların Kimyası",
    "Antik Destanlar: Gılgamış, İlyada ve Mahabharata",
    "Shakespeare'in Eserlerindeki Dramatik Yapı",
    "Dostoyevski ve İnsan Psikolojisi",
    "Modern Romanın Doğuşu",
    "Divan Edebiyatı ve Kaside Sanatı",
    "Şiirde Ölçü ve Kafiye Düzeni",
    "Türk Halk Hikayeleri ve Efsaneleri",
    "Gerçeküstücü Edebiyatın Doğuşu",
    "Gotik Romanlar ve Korku Edebiyatı",
    "Bilim Kurgu Edebiyatı ve Gelecek Kurguları",
    "Epik Şiirin Tarihsel Evrimi",
    "Franz Kafka ve Absürdizm",
    "Latin Amerika Edebiyatında Büyülü Gerçekçilik",
    "Tanzimat Edebiyatı ve İlk Romanlarımız",
    "Distopya Romanları ve Toplum Eleştirisi",
    "Dünya Mitolojilerinde Kahramanlar",
    "Postmodern Roman ve Anlatı Teknikleri",
    "İslam Edebiyatında Mesnevi ve Hikayeler",
    "Klasik Dönem Rus Edebiyatı",
    "İlk Basılı Kitap ve Yayıncılığın Tarihi",
    "Masalların Kökenleri ve Evrimi",
    "Sembolist Şiir ve Dilin Kullanımı",
    "Büyük Fransız Romancıları: Hugo, Balzac, Flaubert",
    "Kurmaca ve Gerçek Arasındaki Sınır",
    "Edebiyatta Melankoli ve Mutsuzluk Temaları",
    "Orta Çağ'da Yazılmış Efsanevi Kitaplar",
    "Savaş Romanları ve Toplumsal Bellek",
    "Çağdaş Türk Edebiyatında Yeni Akımlar",
    "Öykü Yazma Teknikleri ve Kurallar",
    "Edebi Üslup ve Yazarın Kimliği",
  ],
  en: [
    "The Mysterious World of Dinosaurs",
    "Giants in the Sky: Planets and Moons",
    "Light and Optics: How Do Our Eyes See?",
    "The Power of Wind: Storms and Hurricanes",
    "Life Underground: Caves and Earthquakes",
    "Electricity and Magnetism",
    "Glaciers and Polar Regions",
    "Sound Waves: Music and Physics",
    "The Microscopic World: Cells and DNA",
    "How Do Plants Grow? The Secrets of Photosynthesis",
    "The Human Body: How Our Organs Work",
    "The Birth and Death of Stars",
    "The Flow of Time: Clocks and Calendars",
    "Deep-Sea Explorations",
    "Architecture and Engineering: How Are Bridges Built?",
    "The World of Mathematics: Numbers and Geometry",
    "The Rise of Robots",
    "Animals' Survival Strategies",
    "The Human Brain: Intelligence and Memory",
    "Energy Sources: Renewable vs. Fossil Fuels",
    "The Treasure Underground: Minerals and Mines",
    "Meteors and Comets",
    "The Science of Colors: Pigments and Light Spectrum",
    "Poisonous Creatures: Survival Mechanisms",
    "Mysterious Ancient Civilizations",
    "Volcanoes and Rivers of Lava",
    "How is Weather Predicted?",
    "Evolution: From the Distant Past to Today",
    "Pioneering Women in Science",
    "The Water Cycle and Its Importance for Life",
    "Humanity's First Exploration Journeys",
    "The Invisible Force: Atoms and Molecules",
    "Toxic Plants and Their Uses",
    "Wells and Water Sources",
    "The Concept of Infinity in Mathematics",
    "Microbes in the Human Body",
    "Super-Fast Animals: The Cheetah and Others",
    "Flying Creatures: Birds, Bats, and Insects",
    "Surface Tension and the Behavior of Liquids",
    "Life in the Deep Oceans",
    "Sharks: Evolution and Ecology",
    "The Hidden Universe: Dark Matter and Dark Energy",
    "How Do Machines Work?",
    "Superpowers in Comic Books: Science or Fiction?",
    "The Power of Thought: Mind and Neuroscience",
    "Experiments in Zero Gravity",
    "Artificial Intelligence and Future Technologies",
    "Jellyfish and Bioluminescence",
    "Social Insects: Ants and Bees",
    "How Do Electronic Devices Work?",
    "The Biggest Earthquakes in History and Their Effects",
    "Ghost Particles: Neutrinos",
    "The Earth's Layers and Plate Tectonics",
    "The First Computers and Today's Technology",
    "How Do Animals Communicate?",
    "Huge Numbers: What is a Googolplex?",
    "High-Speed Train Technology",
    "The Applications of Sound Waves",
    "Quantum Theory in Physics",
    "Optical Illusions and Perception",
    "Poisonous Mushrooms and Their Role in Nature",
    "Human Evolution and Our Ancestors",
    "The Most Famous Materials in History: Gold, Diamonds, and Uranium",
    "The Role of Insects in Ecosystems",
    "Daily Life of an Astronaut",
    "The Earth's Magnetic Field and the Compass",
    "How Sound Travels in Different Temperatures and Environments",
    "The Moon's Phases and the Tides",
    "Alternative Medicine and Herbal Treatments",
    "Is Life on Mars Possible?",
    "Ancient Chinese Inventions and Science",
    "Camouflage and Adaptation in Animals",
    "The Discovery of DNA and Genetic Engineering",
    "The Science of Lightning",
    "Nanotechnology: Small but Powerful",
    "The Power of Waves: Ocean and Sound Waves",
    "The First Written Languages of Humanity",
    "Wind Turbines and Green Energy",
    "Why Sound Doesn't Travel in Space",
    "Mummy Science: Ancient Egypt and Mummification Techniques",
    "Ancient Roman Water Engineering",
    "Time Travel: Science Fiction or Reality?",
    "Submarines and Sunken Ships",
    "Human Senses and Brain Perception",
    "Agricultural Revolutions and Modern Technology",
    "How Do Flies See?",
    "The Anatomy of the Internet: How Does Data Flow?",
    "Powerful Materials: Carbon Fiber and Graphene",
    "The First Human Homes and Settlements",
    "The Neurological Basis of Emotions",
    "Ant Colonies and Their Organization",
    "Sea Salt and Ocean Currents",
    "From the First Telescopes to Today's Space Telescopes",
    "The Similarities Between the Brain and Artificial Intelligence",
    "Seismic Waves and Earthquake Science",
    "Survival Strategies in the Desert",
    "Deep-Sea Volcanoes and Hydrothermal Vents",
    "The Scientific Accuracy of Science Fiction Movies",
    "How Do Space Stations Work?",
    "Magnetic Fields of Planets and Their Protective Effects",
  ],
  de: [
    "Die Geheimnisvolle Welt der Dinosaurier",
    "Riesen am Himmel : Planeten und Monde",
    "Licht und Optik : Wie sehen unsere Augen?",
    "Die Macht des Windes : Stürme und Hurrikane",
    "Leben unter der Erde : Höhlen und Erdbeben",
    "Elektrizität und Magnetismus",
    "Gletscher und Polarregionen",
    "Schallwellen : Musik und Physik",
    "Die Mikroskopische Welt : Zellen und DNA",
    "Wie wachsen Pflanzen ? Die Geheimnisse der Photosynthese",
    "Der Menschliche Körper : Wie unsere Organe arbeiten",
    "Geburt und Tod der Sterne",
    "Der Fluss der Zeit : Uhren und Kalender",
    "Tiefsee-Erkundungen",
    "Architektur und Ingenieurwesen : Wie werden Brücken gebaut?",
    "Die Welt der Mathematik : Zahlen und Geometrie",
    "Der Aufstieg der Roboter",
    "Überlebensstrategien der Tiere",
    "Das Menschliche Gehirn : Intelligenz und Gedächtnis",
    "Energiequellen : Erneuerbare vs. Fossile Brennstoffe",
    "Der Schatz unter der Erde : Mineralien und Minen",
    "Meteore und Kometen",
    "Die Wissenschaft der Farben : Pigmente und Lichtspektrum",
    "Giftige Kreaturen : Überlebensmechanismen",
    "Geheimnisvolle Antike Zivilisationen",
    "Vulkane und Lavaflüsse",
    "Wie wird das Wetter vorhergesagt?",
    "Evolution : Von der fernen Vergangenheit bis heute",
    "Pionierinnen de la science",
    "Der Wasserkreislauf und seine Bedeutung für das Leben",
    "Die ersten Entdeckungsreisen der Menschheit",
    "Die Unsichtbare Kraft : Atome und Moleküle",
    "Giftige Pflanzen und ihre Verwendung",
    "Brunnen und Wasserquellen",
    "Das Konzept der Unendlichkeit in der Mathematik",
    "Mikroben im menschlichen Körper",
    "Superschnelle Tiere : Der Gepard und andere",
    "Fliegende Kreaturen : Vögel, Fledermäuse und Insekten",
    "Oberflächenspannung und Verhalten von Flüssigkeiten",
    "Leben in den tiefen Ozeanen",
    "Haie : Evolution und Ökologie",
    "Das Verborgene Universum : Dunkle Materie und Dunkle Energie",
    "Wie funktionieren Maschinen?",
    "Superkräfte in Comics : Wissenschaft oder Fiktion?",
    "Die Macht des Denkens : Geist und Neurowissenschaft",
    "Experimente in der Schwerelosigkeit",
    "Künstliche Intelligenz und Zukunftstechnologien",
    "Quallen und Biolumineszenz",
    "Soziale Insekten : Ameisen und Bienen",
    "Wie funktionieren elektronische Geräte?",
    "Die größten Erdbeben der Geschichte und ihre Auswirkungen",
    "Geisterpartikel : Neutrinos",
    "Die Schichten der Erde und Plattentektonik",
    "Die ersten Computer und heutige Technologie",
    "Wie kommunizieren Tiere?",
    "Riesige Zahlen : Was ist ein Googolplex?",
    "Hochgeschwindigkeits-Zugtechnologie",
    "Anwendungen von Schallwellen",
    "Quantentheorie in der Physik",
    "Starke Materialien: Kohlefaser und Graphen",
    "Die ersten menschlichen Häuser und Siedlungen",
    "Die neurologische Basis von Emotionen",
    "Ameisenkolonien und ihre Organisation",
    "Meersalz und Meeresströmungen",
    "Von den ersten Teleskopen zu heutigen Weltraumteleskopen",
    "Die Ähnlichkeiten zwischen Gehirn und künstlicher Intelligenz",
    "Seismische Wellen und Erdbebenwissenschaft",
    "Überlebensstrategien in der Wüste",
    "Tiefseevulkane und hydrothermale Quellen",
    "Die wissenschaftliche Genauigkeit von Science-Fiction-Filmen",
  ],
  fr: [
    "Comment fonctionnent les stations spatiales ?",
    "Champs magnétiques des planètes et effets protecteurs",
    "Philosophes grecs antiques et écoles de pensée",
    "L’essor de l’Empire ottoman et ses conquêtes",
    "Contributions de la Renaissance à l’art et à la science",
    "La révolution industrielle : l’essor des machines",
    "Les explorations vikings",
    "La vie quotidienne dans la Rome antique",
    "La route de la soie et les échanges culturels",
    "Les plus grandes guerres de l’histoire",
    "La médecine de l’Égypte ancienne",
    "Conséquences de la guerre d’indépendance américaine",
    "Les Hittites : le mystérieux royaume d’Anatolie",
    "Alexandre le Grand et son héritage culturel",
    "Gutenberg et l’invention de l’imprimerie",
    "Les campagnes de Napoléon qui ont changé l’Europe",
    "Les premiers mouvements féministes et l’histoire des droits des femmes",
    "Le système administratif de l’Empire mongol",
    "Civilisations inca et maya",
    "Les croisades : causes et conséquences",
    "La révolution industrielle et les droits des travailleurs",
    "La guerre froide : rivalité entre les USA et l’URSS",
    "Conséquences de la Seconde Guerre mondiale sur la science et la technologie",
    "Éducation et culture des médersas dans l’Empire ottoman",
    "La peste noire : pandémie médiévale en Europe",
    "Royaumes africains : Mali, Songhaï et Bénin",
    "La Mésopotamie antique et les premiers systèmes d’écriture",
    "La divine proportion et son usage dans l’art",
    "Théorie du chaos et effet papillon",
    "Paradoxes de l’infini en mathématiques",
    "Le nombre d’Euler et sa place dans la nature",
    "Géométrie et formes dans la nature",
    "Fractales et motifs répétitifs",
    "Les premiers algorithmes informatiques et Alan Turing",
    "Théorie des nombres et nombres premiers",
    "Statistiques et probabilités",
    "Équations différentielles et leur importance en physique",
    "La découverte et le calcul de Pi",
    "Verschlüsselungsmethoden durch die Geschichte",
    "Le dernier théorème de Fermat",
    "Triangles et développement de la trigonométrie",
    "Diagrammes de Venn et systèmes logiques",
    "Nombres irrationnels et leur application dans le monde réel",
    "Grands nombres : googolplex et au-delà",
    "Théorie des jeux et stratégie en mathématiques",
    "Mathématiques et cryptographie",
    "Dérivée et intégrale : la naissance du calcul infinitésimal",
    "La découverte des éléments et le tableau périodique",
    "La chimie au quotidien",
    "La découverte et l’utilisation de la radioactivité",
    "Liquides et gaz : pression et mouvement",
    "Plastique et son impact environnemental",
    "Acides et bases : équilibre du pH",
    "Réactions endothermiques et exothermiques",
    "Liaisons chimiques et structure de la matière",
    "ADN et chimie génétique",
    "Isotopes des éléments et leurs applications",
    "Énergie et chaleur dans les réactions chimiques",
    "Polymères et leur rôle dans la vie quotidienne",
    "Phosphore et produits chimiques explosifs",
    "Chimie organique et composés du carbone",
    "Métallurgie et utilisation des métaux à travers l’histoire",
    "Pigments colorés et leur chimie",
    "La découverte des antibiotiques et leur chimie",
    "Facteurs influençant la vitesse des réactions chimiques",
    "Phénomènes chimiques dans l’atmosphère",
    "Chimie alimentaire : ce que nous mangeons",
    "Prix Nobel de chimie et grandes découvertes",
    "Liaisons hydrogène et leur importance vitale",
    "L’utilisation de la chimie en médecine",
    "Les premiers composés chimiques synthétiques",
    "La chimie du savon et des détergents",
    "Épopées antiques : Gilgamesh, Iliade et Mahabharata",
    "Structure dramatique dans les œuvres de Shakespeare",
    "Dostoïevski et la psychologie humaine",
    "La naissance du roman moderne",
    "Littérature divan et l’art du qasida",
    "Mètre et schéma de rimes en poésie",
    "Contes et légendes populaires turcs",
    "La naissance de la littérature surréaliste",
    "Romans gothiques et littérature d’horreur",
    "Littérature de science-fiction et visions du futur",
    "L’évolution historique de l’épopée",
    "Franz Kafka et l’absurdisme",
    "Réalisme magique dans la littérature latino-américaine",
    "Littérature de Tanzimat et nos premiers romans",
    "Romans dystopiques et critique sociale",
    "Héros dans les mythologies du monde",
    "Romans postmodernes et techniques narratives",
    "Mesnevi et récits dans la littérature islamique",
    "Littérature russe de la période classique",
    "Le premier livre imprimé et l’histoire de l’édition",
    "Origines et évolution des contes de fées",
    "Poésie symboliste et usage du langage",
    "Grands romanciers français : Hugo, Balzac, Flaubert",
    "La frontière entre fiction et réalité",
    "Mélancolie et malheur comme thèmes littéraires",
    "Livres légendaires du Moyen Âge",
    "Romans de guerre et mémoire collective",
    "Nouvelles tendances dans la littérature turque contemporaine",
    "Techniques et règles de l’écriture de récits",
    "Style littéraire et identité de l’auteur",
  ],
  es: [
    "¿Cómo funcionan las estaciones espaciales?",
    "Campos magnéticos de los planetas y efectos protectores",
    "Filósofos griegos antiguos y escuelas de pensamiento",
    "El auge del Imperio Otomano y sus conquistas",
    "Contribuciones del Renacimiento al arte y la ciencia",
    "La revolución industrial: el auge de las máquinas",
    "Las exploraciones vikingas",
    "La vida cotidiana en la antigua Roma",
    "La ruta de la seda y los intercambios culturales",
    "Las guerras más grandes de la historia",
    "La medicina en el antiguo Egipto",
    "Consecuencias de la guerra de independencia estadounidense",
    "Los hititas: el misterioso reino de Anatolia",
    "Alejandro Magno y su legado cultural",
    "Gutenberg y la invención de la imprenta",
    "Las campañas de Napoleón que cambiaron Europa",
    "Los primeros movimientos feministas y la historia de los derechos de las mujeres",
    "El sistema administrativo del Imperio Mongol",
    "Civilizaciones inca y maya",
    "Las cruzadas: causas y consecuencias",
    "La revolución industrial y los derechos de los trabajadores",
    "La guerra fría: rivalidad entre EE. UU. y la URSS",
    "Consecuencias de la Segunda Guerra Mundial en la ciencia y la tecnología",
    "Educación y cultura de las madrasas en el Imperio Otomano",
    "La peste negra: pandemia medieval en Europa",
    "Reinos africanos: Mali, Songhai y Benín",
    "La antigua Mesopotamia y los primeros sistemas de escritura",
    "La proporción áurea y su uso en el arte",
    "Teoría del caos y efecto mariposa",
    "Paradojas del infinito en matemáticas",
    "El número de Euler y su lugar en la naturaleza",
    "Geometría y formas en la naturaleza",
    "Fractales y patrones repetitivos",
    "Los primeros algoritmos informáticos y Alan Turing",
    "Teoría de números y números primos",
    "Estadística y probabilidad",
    "Ecuaciones diferenciales y su importancia en la física",
    "El descubrimiento y cálculo de Pi",
    "Métodos de cifrado a lo largo de la historia",
    "El último teorema de Fermat",
    "Triángulos y desarrollo de la trigonometría",
    "Diagramas de Venn y sistemas lógicos",
    "Números irracionales y su aplicación en el mundo real",
    "Números grandes: googolplex y más allá",
    "Teoría de juegos y estrategia en matemáticas",
    "Matemáticas y criptografía",
    "Derivada e integral: el nacimiento del cálculo infinitesimal",
    "El descubrimiento de los elementos y la tabla periódica",
    "La química en la vida cotidiana",
    "El descubrimiento y uso de la radiactividad",
    "Líquidos y gases: presión y movimiento",
    "El plástico y su impacto ambiental",
    "Ácidos y bases: equilibrio del pH",
    "Reacciones endotérmicas y exotérmicas",
    "Enlaces químicos y estructura de la materia",
    "ADN y química genética",
    "Isótopos de los elementos y sus aplicaciones",
    "Energía y calor en las reacciones químicas",
    "Polímeros y su papel en la vida cotidiana",
    "Fósforo y productos químicos explosivos",
    "Química orgánica y compuestos de carbono",
    "Metalurgia y uso de los metales a lo largo de la historia",
    "Pigmentos de colores y su química",
    "El descubrimiento de los antibióticos y su química",
    "Factores que influyen en la velocidad de las reacciones químicas",
    "Fenómenos químicos en la atmósfera",
    "Química alimentaria: lo que comemos",
    "Premios Nobel de química y grandes descubrimientos",
    "Enlaces de hidrógeno y su importancia vital",
    "El uso de la química en la medicina",
    "Los primeros compuestos químicos sintéticos",
    "La química del jabón y los detergentes",
    "Epopeyas antiguas: Gilgamesh, Ilíada y Mahabharata",
    "Estructura dramática en las obras de Shakespeare",
    "Dostoievski y la psicología humana",
    "El nacimiento de la novela moderna",
    "Literatura diván y el arte del qasida",
    "Métrica y esquema de rima en la poesía",
    "Cuentos y leyendas populares turcas",
    "El nacimiento de la literatura surrealista",
    "Novelas góticas y literatura de terror",
    "Literatura de ciencia ficción y visiones del futuro",
    "La evolución histórica de la epopeya",
    "Franz Kafka y el absurdismo",
    "Realismo mágico en la literatura latinoamericana",
    "Literatura del Tanzimat y nuestras primeras novelas",
    "Novelas distópicas y crítica social",
    "Héroes en las mitologías del mundo",
    "Novelas posmodernas y técnicas narrativas",
    "Mesnevi y relatos en la literatura islámica",
    "Literatura rusa del período clásico",
    "El primer libro impreso y la historia de la edición",
    "Orígenes y evolución de los cuentos de hadas",
    "Poesía simbolista y uso del lenguaje",
    "Grandes novelistas franceses: Hugo, Balzac, Flaubert",
    "La frontera entre ficción y realidad",
    "Melancolía y desgracia como temas literarios",
    "Libros legendarios de la Edad Media",
    "Novelas de guerra y memoria colectiva",
    "Nuevas tendencias en la literatura turca contemporánea",
    "Técnicas y reglas de la escritura de relatos",
    "Estilo literario e identidad del autor",
  ],
};

const CHARACTER_LIMIT = 200000;

const CreatePodcastCard = ({
  onRegenClick,
  openPdfCustomizationModal,
}: {
  onRegenClick: (
    podcastId: number,
    podcastGenerationType: CreatePodcastFromReferenceRequestDtoGenerationType,
  ) => void;
  onAssignClick: (podcastId: number, podcastTitle: string) => void;
  openPdfCustomizationModal?: (podcastId: number | undefined) => void;
}) => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [topic, setTopic] = useState("");
  const [podcastId, setPodcastId] = useState<number | undefined>(undefined);
  const [showNewPodcast, setShowNewPodcast] = useState(false);
  const [latestPodcast, setLatestPodcast] = useState<PodcastResponseDto | null>(
    null,
  );
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [generatorMode, setGeneratorMode] = useState<"TEXT" | "PDF">("TEXT");
  const [uploadedPdfIds, setUploadedPdfIds] = useState<number[]>([]);
  const [uploadError, setUploadError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [outputLanguage, setOutputLanguage] = useState<string>(i18n.language);
  const [cefrLevel, setCefrLevel] = useState<CreatePodcastRequestDtoCefrLevel>(
    CreatePodcastRequestDtoCefrLevel.A1,
  );
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Listen for regeneration completion to switch to the new podcast and open modal
  useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<{ id: number }>;
      if (custom.detail?.id) {
        // Close any open modal first so we don't stack and notify card instances
        setIsModalOpen(false);
        window.dispatchEvent(new CustomEvent("podcast:close-player"));
        getPodcast(custom.detail.id).then((p) => {
          setLatestPodcast(p as any);
          setShowNewPodcast(true);
          setIsModalOpen(true);
        });
      }
    };
    const closeHandler = () => setIsModalOpen(false);
    window.addEventListener("podcast:regenerated", handler as EventListener);
    window.addEventListener("podcast:close-player", closeHandler);
    return () => {
      window.removeEventListener(
        "podcast:regenerated",
        handler as EventListener,
      );
      window.removeEventListener("podcast:close-player", closeHandler);
    };
  }, []);

  // (duplicate listener removed)

  // Theme gradient classes for OutputLanguageSelector
  const gradientClasses = {
    red: "from-red-600 to-red-700",
    orange: "from-orange-600 to-orange-700",
    blue: "from-blue-600 to-blue-700",
    teal: "from-teal-600 to-teal-700",
    cyan: "from-cyan-600 to-cyan-700",
    rose: "from-rose-600 to-rose-700",
    stone: "from-stone-600 to-stone-700",
    lime: "from-lime-600 to-lime-700",
    terra: "from-amber-600 to-orange-700",
    purple: "from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400",
    amber: "from-amber-600 to-amber-700",
    emerald: "from-emerald-600 to-emerald-700",
    violet: "from-violet-600 to-violet-700",
    slate: "from-slate-600 to-slate-700",
    yellow: "from-yellow-600 to-yellow-700",
    fuchsia: "from-fuchsia-600 to-fuchsia-700",
    zinc: "from-zinc-600 to-zinc-700",
    green: "from-green-600 to-green-700",
    coral: "from-pink-600 to-orange-600",
    mint: "from-green-400 to-teal-500",
    lavender: "from-purple-400 to-blue-500",
    sunset: "from-orange-500 to-pink-600",
    ocean: "from-blue-500 to-teal-600",
    bloom: "from-pink-500 to-purple-600",
    cream: "from-amber-200 to-orange-200",
  };

  // CEFR level options
  const cefrLevelOptions = [
    { value: CreatePodcastRequestDtoCefrLevel.PRE_A1, label: "Pre-A1" },
    { value: CreatePodcastRequestDtoCefrLevel.A1, label: "A1" },
    { value: CreatePodcastRequestDtoCefrLevel.A2, label: "A2" },
    { value: CreatePodcastRequestDtoCefrLevel.B1, label: "B1" },
    { value: CreatePodcastRequestDtoCefrLevel.B2, label: "B2" },
    { value: CreatePodcastRequestDtoCefrLevel.C1, label: "C1" },
    { value: CreatePodcastRequestDtoCefrLevel.C2, label: "C2" },
  ];

  const steps = useMemo(() => {
    if (i18n.language === "tr")
      return [
        "Konu analiz ediliyor ve bilgi toplanıyor...",
        "Görsel yapı oluşturuluyor...",
        "Kavramlar ve bağlantılar düzenleniyor...",
        "Zihin haritası oluşturma işlemi tamamlanıyor...",
        "Ses oluşturma işlemi tamamlanıyor...",
      ];
    if (i18n.language === "fr")
      return [
        "Analyse du sujet et collecte d'informations...",
        "Création de la structure visuelle...",
        "Organisation des concepts et connexions...",
        "Finalisation de la génération de la carte mentale...",
        "Génération de l'audio...",
      ];
    if (i18n.language === "es")
      return [
        "Analizando el tema y recopilando información...",
        "Creando la estructura visual...",
        "Organizando conceptos y conexiones...",
        "Finalizando la generación de la carta mental...",
        "Generando el audio...",
      ];
    if (i18n.language === "de")
      return [
        "Thema analysieren und Informationen sammeln...",
        "Visuelle Struktur erstellen...",
        "Konzepte und Verbindungen organisieren...",
        "Mind Map-Generierung abschließen...",
        "Audio generieren...",
      ];
    return [
      "Analyzing topic and gathering information...",
      "Creating visual structure...",
      "Organizing concepts and connections...",
      "Finalizing mind map generation...",
      "Generating audio...",
    ];
  }, [i18n.language]);

  const randomExampleTopics = useMemo(() => {
    const topics =
      exampleTopics[i18n.language as "tr" | "en" | "de" | "fr" | "es"] ||
      exampleTopics["en"];
    return topics.sort(() => Math.random() - 0.5).slice(0, 8);
  }, [i18n.language]);

  // Check if form is valid for submission
  const isFormValid = useMemo(() => {
    if (generatorMode === "TEXT") {
      return topic?.trim().length > 0;
    } else {
      return uploadedPdfIds.length > 0;
    }
  }, [generatorMode, topic, uploadedPdfIds]);

  const { data: podcast } = useGetPodcast(podcastId!, {
    query: {
      enabled: podcastId !== undefined,

      refetchInterval: (query) => {
        if (query?.state?.data?.status === "FAILED") {
          return false;
        }
        if (!query?.state?.data?.transcript || !query?.state?.data?.url) {
          return 5000;
        }
        if (!query?.state?.data?.imageUrl) {
          return 3000;
        }

        return false;
      },
      queryFn: async () => {
        const response = await getPodcast(podcastId!);
        if (response?.imageUrl) {
          setIsImageLoading(false);
        } else {
          setIsImageLoading(true);
        }

        if (response?.status === "FAILED") {
          setPodcastId(undefined);
          setTopic("");
          setUploadedPdfIds([]);

          addToast({
            title: t("podcast.errors.generationFailed"),
            description: t("podcast.errors.tryAgain"),
            variant: "error",
            duration: 5000,
          });
        }

        return response;
      },
    },
  });
  useEffect(() => {
    if (podcast?.transcript && podcast?.url) {
      console.log("Podcast data:", podcast);
      console.log("Questions:", podcast.questions);
      setLatestPodcast(podcast);
      setShowNewPodcast(true);
      queryClient.invalidateQueries({
        queryKey: getGetUserPodcastsQueryKey(),
      });
      setPodcastId(undefined);
      setTopic("");
      setUploadedPdfIds([]);
    }
  }, [podcast?.transcript, podcast?.url]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Trim the topic and check if it's valid
    const trimmedTopic = topic?.trim();

    if (!trimmedTopic && !uploadedPdfIds?.length) return;

    if (generatorMode === "TEXT" && trimmedTopic?.length > CHARACTER_LIMIT) {
      addToast({
        title: t("podcast.errors.tooLong"),
        description: t("podcast.errors.characterLimit", {
          limit: CHARACTER_LIMIT.toLocaleString(),
        }),
        variant: "error",
        duration: 5000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const podcast = await createPodcast({
        prompt: generatorMode === "TEXT" ? trimmedTopic : undefined,
        environment: getEnvironment(),
        generationType:
          generatorMode === "TEXT"
            ? CreatePodcastRequestDtoGenerationType.PROMPT
            : CreatePodcastRequestDtoGenerationType.TEACHER_DOCUMENT,
        teacherDocumentIds: uploadedPdfIds || undefined,
        contentLanguage:
          outputLanguage as CreatePodcastRequestDtoContentLanguage,
        cefrLevel: isCheckOutputLanguage(outputLanguage),
      });
      setPodcastId(podcast.id);
      dispatch(updateLastUsedTools("podcasts"));
      track(
        {
          category: "ContentGeneration",
          action: "podcast.generate",
          label: "Podcast generation click",
          nonInteraction: false,
        },
        {
          location: "/podcasts/generate",
          element: "generate-button",
          podcastGenerationType: generatorMode,
          podcastGenerationPrompt: trimmedTopic,
          podcastGenerationFileIds: uploadedPdfIds.join(","),
        },
      );
    } catch (error: any) {
      if (
        error.response?.data.message ===
        "Content contains inappropriate language"
      ) {
        addToast({
          title: t("common.inappropriateContent.title"),
          description: t("common.inappropriateContent.description"),
          variant: "error",
          duration: 3000,
        });
      } else {
        addToast({
          title: t("podcast.errors.generationFailed"),
          description: t("podcast.errors.tryAgain"),
          variant: "error",
          duration: 5000,
        });
      }
      setPodcastId(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPodcast = () => {
    setShowNewPodcast(false);
  };

  const { trackEvent: track } = trackEvent();
  const handleClickPredifined = (example: string) => {
    track(
      {
        category: "ContentPredefined",
        action: "podcast.predefined",
        label: "Podcast predefined click",
        nonInteraction: false,
      },
      {
        location: "/podcast/predefined",
        element: "predefined-button",
      },
    );
    console.log(example);
    setTopic(example);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const isCheckOutputLanguage = (language: string) => {
    return ["en", "fr", "de", "es"].includes(language)
      ? cefrLevel
      : CreatePodcastRequestDtoCefrLevel.A1;
  };

  if (showNewPodcast) {
    return (
      <div className="">
        <div className="animate-fadeIn relative">
          <div className="w-full mx-auto xl:w-2/3">
            <PodcastAudioCard
              id={latestPodcast?.id}
              url={latestPodcast?.url}
              imageUrl={latestPodcast?.imageUrl}
              isImageLoading={isImageLoading}
              name={latestPodcast?.title}
              transcript={latestPodcast?.transcript}
              createdAt={latestPodcast?.createdAt}
              author={latestPodcast?.authorName}
              onRegenClick={(podcastId, podcastGenerationType) => {
                if (podcastId) {
                  onRegenClick(podcastId, podcastGenerationType);
                }
              }}
              openPdfCustomizationModal={openPdfCustomizationModal}
              podcastGenerationType={
                latestPodcast?.teacherDocuments &&
                latestPodcast?.teacherDocuments?.length > 0
                  ? CreatePodcastFromReferenceRequestDtoGenerationType.TEACHER_DOCUMENT
                  : CreatePodcastFromReferenceRequestDtoGenerationType.PROMPT
              }
              hideEditButton={true}
            />

            {/* Action Buttons below podcast card */}
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                onClick={() => handleNewPodcast()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 rounded-lg transition-all shadow-sm hover:shadow-md"
              >
                <RefreshCcw className="w-4 h-4" />
                {t("podcast.generateNew")}
              </button>
              <button
                onClick={() =>
                  onRegenClick(
                    latestPodcast?.id || 0,
                    latestPodcast?.teacherDocuments &&
                      latestPodcast?.teacherDocuments?.length > 0
                      ? CreatePodcastFromReferenceRequestDtoGenerationType.TEACHER_DOCUMENT
                      : CreatePodcastFromReferenceRequestDtoGenerationType.PROMPT,
                  )
                }
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-all shadow-sm hover:shadow-md"
              >
                <Bot className="w-4 h-4" />
                {t("assignments.generator.preview.regenerate")}
              </button>
            </div>
          </div>
        </div>

        {isModalOpen && latestPodcast && (
          <PodcastPlayerModal
            podcast={latestPodcast}
            onClose={closeModal}
            onRegenClick={(podcastId, generationType) =>
              onRegenClick(podcastId, generationType)
            }
            podcastGenerationType={
              latestPodcast?.teacherDocuments &&
              latestPodcast?.teacherDocuments?.length > 0
                ? CreatePodcastFromReferenceRequestDtoGenerationType.TEACHER_DOCUMENT
                : CreatePodcastFromReferenceRequestDtoGenerationType.PROMPT
            }
            openPdfCustomizationModal={openPdfCustomizationModal}
          />
        )}
      </div>
    );
  }

  return (
    <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-md rounded-lg p-4 shadow-md border border-white/20 dark:border-gray-700/20 relative">
      <div className="relative generate-podcast-form">
        <div className="space-y-3 sm:space-y-4">
          {podcastId === undefined && (
            <div className="space-y-3">
              <Text
                variant="h4"
                className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400"
              >
                {t("podcast.selectInputType")}
              </Text>

              <div className="grid grid-cols-2 gap-4">
                <Button
                  onClick={() => setGeneratorMode("TEXT")}
                  rounded="lg"
                  bgColor={`${
                    generatorMode === "TEXT" &&
                    "bg-purple-50 dark:bg-purple-900/20"
                  }`}
                  className={`p-3 border-2 ${
                    generatorMode === "TEXT"
                      ? "border-purple-500"
                      : "border-gray-200 hover:border-purple-400"
                  }`}
                >
                  <div className="flex items-center justify-center space-x-1">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm">{t("podcast.textInput")}</span>
                  </div>
                </Button>

                <Button
                  onClick={() => setGeneratorMode("PDF")}
                  className={`p-3 border-2 ${
                    generatorMode === "PDF"
                      ? "border-purple-500"
                      : "border-gray-200 hover:border-purple-400"
                  }`}
                  bgColor={`${
                    generatorMode === "PDF" &&
                    "bg-purple-50 dark:bg-purple-900/20"
                  }`}
                  rounded="lg"
                >
                  <div className="flex items-center justify-center space-x-1">
                    <FileText className="w-4 h-4" />
                    <span className="text-sm">{t("podcast.pdfInput")}</span>
                  </div>
                </Button>
              </div>

              {generatorMode === "TEXT" ? (
                <div className="space-y-3">
                  <div className="flex flex-col lg:flex-row gap-2">
                    <div className="flex-1">
                      <Text
                        variant="h4"
                        className="text-gray-700 dark:text-gray-300 font-medium mb-1"
                      >
                        {t("transferActivities.form.topic")}
                      </Text>
                      <input
                        type="text"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder={t("podcast.placeholder")}
                        className="w-full p-2 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800/80 shadow-sm focus:ring-1 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                      />
                    </div>

                    {/* Output Language Selector */}
                    <div className="flex flex-col lg:w-40">
                      <Text
                        variant="h4"
                        className="text-gray-700 dark:text-gray-300 font-medium mb-1"
                      >
                        {t("hooks.form.outputLanguage")}
                      </Text>
                      <OutputLanguageSelector
                        selectedLanguage={outputLanguage}
                        onLanguageChange={setOutputLanguage}
                        themeColor="purple"
                        gradientClasses={gradientClasses}
                      />
                    </div>

                    {["en", "fr", "de", "es"].includes(outputLanguage) && (
                      <div className="flex flex-col lg:w-40">
                        <Text
                          variant="h4"
                          className="text-gray-700 dark:text-gray-300 font-medium mb-1"
                        >
                          {t("podcast.cefrLevel")}
                        </Text>
                        <CEFRLevelSelector
                          selectedLevel={cefrLevel}
                          onLevelChange={(level) =>
                            setCefrLevel(
                              level as CreatePodcastRequestDtoCefrLevel,
                            )
                          }
                          themeColor="purple"
                          gradientClasses={gradientClasses}
                          cefrLevelOptions={cefrLevelOptions}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1 mb-3 topic-suggestion">
                    {randomExampleTopics.map((example) => (
                      <Button
                        key={example}
                        type="button"
                        onClick={() => handleClickPredifined(example)}
                        data-testid="topic-suggestion"
                        variant="solid"
                        size="sm"
                        rounded="full"
                        textColor="text-gray-700 dark:text-gray-300"
                        bgColor="bg-white/80 dark:bg-gray-800/80 hover:bg-gray-50 dark:hover:bg-gray-700/80"
                      >
                        {example}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <FileUploader
                    onUploadSuccess={(_, response) => {
                      setUploadedPdfIds([response?.id!]);
                      setTopic(
                        response?.fileName?.replace(/\.[^/.]+$/, "") ?? "",
                      );
                    }}
                    onFileDelete={() => {
                      setUploadedPdfIds([]);
                      setTopic("");
                    }}
                    onUploadError={(error) => setUploadError(error)}
                  />
                  {uploadError && (
                    <Text variant="p" status="error" size="sm">
                      {uploadError}
                    </Text>
                  )}

                  <div className="flex flex-col lg:flex-row gap-2">
                    {/* Output Language Selector for PDF mode */}
                    <div className="flex flex-col lg:w-40">
                      <Text
                        variant="h4"
                        className="text-gray-700 dark:text-gray-300 font-medium mb-1"
                      >
                        {t("hooks.form.outputLanguage")}
                      </Text>
                      <OutputLanguageSelector
                        selectedLanguage={outputLanguage}
                        onLanguageChange={setOutputLanguage}
                        themeColor="purple"
                        gradientClasses={gradientClasses}
                      />
                    </div>

                    {/* CEFR Level Selector for PDF mode - Only show for English */}
                    {["en", "fr", "de", "es"].includes(outputLanguage) && (
                      <div className="flex flex-col lg:w-40">
                        <Text
                          variant="h4"
                          className="text-gray-700 dark:text-gray-300 font-medium mb-1"
                        >
                          {t("podcast.cefrLevel")}
                        </Text>
                        <CEFRLevelSelector
                          selectedLevel={cefrLevel}
                          onLevelChange={(level) =>
                            setCefrLevel(
                              level as CreatePodcastRequestDtoCefrLevel,
                            )
                          }
                          themeColor="purple"
                          gradientClasses={gradientClasses}
                          cefrLevelOptions={cefrLevelOptions}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <Button
            variant="solid"
            data-testid="generate-podcast-button"
            size="md"
            rounded="lg"
            fullWidth
            bgColor="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            textColor="text-white"
            className="shadow-md hover:shadow-lg transform hover:-translate-y-0.5 focus:ring-2 focus:ring-purple-500/50"
            disabled={!isFormValid || podcastId !== undefined || isLoading}
            loading={isLoading}
            onClick={handleSubmit}
          >
            {podcastId !== undefined
              ? t("podcast.generating")
              : t("podcast.generateAudioLesson")}
          </Button>

          {podcastId !== undefined && (
            <LoadingContainer
              showSteps={steps}
              title={t("podcast.generating")}
              themeColor="purple"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatePodcastCard;
